import groupBy from 'lodash/groupBy';
import remove from 'lodash/remove';
import { ChangeEvent } from 'react';
import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';
import { isMediaFile, isMediaItem, Nullable, pluralize } from '../../core';
import { UploadTask } from '../../services/upload';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, inputGroup, InputGroupState, InputState } from '../../components/input';
import { createArray } from '../../core/array';
import { UploadFileItemState } from './uploadFileItemState';
import { groupRecordingSegments } from './recordingSegments';
import { GroupCriteria, UploadTaskGroup } from '../../services/upload/uploadTaskGroup';

type Props = BindingProps<{
  teamId?: string,
  jobId?: string,
  folderId?: string,
}>;

/**
 * Controller for the UploadFile component
 */
export class UploadFileState
  extends StoreNode {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      stagedItems: computed,
      activeItems: computed,
      completedItems: computed,
      isReplace: computed,
      teamId: computed,
      folderId: computed,
      jobId: computed,
      teamName: computed,
      job: computed,
      isHovered: observable,
      isDragHovered: observable,
      expandedBoxes: observable,
      expandProgressBox: action,
      collapseProgressBox: action,
      toggleProgressBox: action,
      dragError: computed,
      dragErrorMessage: computed,
      dragDropMessage: computed,
      teamGroupedTasks: computed,
      tasksByTeam: computed,
      taskTeamIds: computed,
      hasReplaceCurrentTask: computed,
      replaceLocked: computed,
      showStagingForm: computed,
      showProgressBox: computed,
      stagingBoxesCount: computed,
      isDisabled: computed,
      handlePointerEnter: action,
      handlePointerLeave: action,
      handleFileChange: action,
      handleFileClick: action,
      handleDragEnter: action,
      handleDragLeave: action,
      handleDrop: action,
      setUploadInput: action,
      addFileItem: action,
      splitFileItem: action,
      removeFileItem: action,
      submit: action,
      init: action,
      reset: action,
      resetForm: action,
    });

    this.form = inputGroup(this, {
      name: 'form',
      inputs: () =>
        this.stagedItems.map((el: UploadFileItemState) => el.form),
      isSubmitDisabled: () => {
        return (
          this.form.inputs.some(input =>
            // @ts-expect-error -- InputState.inputs exists on InputGroupState but not on the union type
            input.inputs.some(input => input.status === 'error' && input.isTouched && !input.isFocused)
          ) ||
          this.form.isSubmitting ||
          // this.form.hasTouchedErrorInputs ||
          this.form.isEmpty ||
          (this.isReplace && this.hasReplaceCurrentTask)
        );
      }
    });
  }

  readonly form: InputGroupState;

  get stagedItems(): UploadFileItemState[] {
    return this.fileInputList.filter(item => item.showInStaging);
  }

  get activeItems(): UploadFileItemState[] {
    return this.fileInputList.filter(item => item.isActive);
  }

  get completedItems(): UploadFileItemState[] {
    return this.fileInputList.filter(item => item.isCompleted);
  }

  get isReplace(): boolean {
    return !!this.jobId;
  }

  // #region Resolved props
  get teamId(): Nullable<string> {
    return this.resolvedProps.teamId;
  }
  get jobId(): Nullable<string> {
    return this.resolvedProps.jobId;
  }
  get folderId(): Nullable<string> {
    return this.resolvedProps.folderId;
  }
  // #endregion

  get teamName(): Nullable<string> {
    if (!this.teamId) return null;

    return this.store.teamManager.strictGetTeam(this.teamId).name;
  }
  get job() {
    return this.store.jobManager.maybeGetJob(this.jobId);
  }

  isHovered: boolean = false;
  isDragHovered: boolean = false;
  readonly dragItems = observable.array<DataTransferItem>();

  // @observable isExpanded: boolean = true;
  expandedBoxes: string[] = [];

  getIsExpanded(id: string): boolean {
    return this.expandedBoxes.findIndex(el => el === id) > -1;
  }

  expandProgressBox = (id: string) => {
    this.expandedBoxes.push(id);
  }

  collapseProgressBox = (id: string) => {
    remove(this.expandedBoxes, el => el === id);
  }

  toggleProgressBox = (id: string) => {
    if (this.expandedBoxes.findIndex(el => el === id) > -1)
      this.collapseProgressBox(id);
    else
      this.expandProgressBox(id);
  }

  get dragError(): boolean {
    return !!this.dragErrorMessage;
  }

  get dragErrorMessage(): Nullable<string> {
    // check if all files are video files
    const items = [...this.dragItems];

    const nonMediaItems = items.filter(item => !isMediaItem(item));
    if (nonMediaItems.length > 0) {
      return pluralize(nonMediaItems.length, 'item is not a media file', 'items are not media files');
    }

    if (this.isReplace && items.length > 0) {
      return 'Only one item supported for replacing the video';
    }

    return null;
  }

  get dragDropMessage(): Nullable<string> {
    const items = [...this.dragItems];
    if (items.length === 0)
      return `Drop files here`; // to avoid glitching on drag leave

    return `Drop ${pluralize(items.length, 'file', 'files')} here`;
  }

  /** The list of tasks, regardless of status, which have been initiated from the current session. */
  readonly pageSessionTasks = observable.array<UploadTask>();

  get teamGroupedTasks(): UploadTaskGroup[] {
    const criteria: GroupCriteria = 'teamId';
    const groups = groupBy(this.pageSessionTasks, criteria);
    return Object.keys(groups).map(key => {
      const value = groups[key];
      const taskGroup = new UploadTaskGroup(this.store, {
        groupBy: criteria,
        groupId: key,
        tasks: value
      });
      return taskGroup;
    });
  }

  get tasksByTeam() {
    return groupBy(this.pageSessionTasks, 'teamId')
  }

  get taskTeamIds() {
    return Object.keys(this.tasksByTeam);
  }

  // @computed
  // get visibleTasks(): UploadTask[] {
  //   const pendingTasks: UploadTask[] = this.store?.uploadService?.pendingTasks || [];
  //   return uniq(pendingTasks.concat([...this.pageSessionTasks]))
  //     .sort((a, b) => (+a.createdAt) - (+b.createdAt));
  // }

  get hasReplaceCurrentTask(): boolean {
    return this.pageSessionTasks.some(task => task.jobId === this.jobId && task.status !== 'canceled');
  }

  get replaceLocked(): boolean {
    return this.hasReplaceCurrentTask;
  }

  readonly fileInputList: IObservableArray<UploadFileItemState> = createArray<UploadFileItemState>(true) as IObservableArray;

  readonly uploadButton: InputState = input(this, {
    name: 'uploadButton',
    disabled: () => {
      return (
        this.form?.isSubmitting ||
        this.form?.hasTouchedErrorInputs
      );
    }
  });

  get showStagingForm(): boolean {
    return this.stagedItems.length > 0;
  }

  get showProgressBox(): boolean {
    return this.teamGroupedTasks && this.teamGroupedTasks.length > 0;
  }

  get stagingBoxesCount(): number {
    let counter = 0;

    if (this.showStagingForm)
      counter += 1;

    counter += this.teamGroupedTasks.length;

    return counter;
  }

  get isDisabled(): boolean {
    return this.jobId
      ? this.store?.uploadService?.pendingTasks.some(task => task.jobId === this.jobId)
      : false;
  }

  handlePointerEnter(_evt: PointerEvent) {
    this.isHovered = true;
  }
  handlePointerLeave(_evt: PointerEvent) {
    this.isHovered = false;
  }
  handleFileChange(evt: ChangeEvent<HTMLInputElement>) {
    this.setUploadInput(evt.target.files);
    // @ts-expect-error -- resetting input.value to null clears the file selection so onChange fires again for same file
    evt.target.value = null;
  }
  handleFileClick(_evt: PointerEvent) { /* noop */ }

  handleDragEnter(evt: DragEvent) {
    if (this.isDisabled)
      return;

    this.isDragHovered = true;
    const items = evt.dataTransfer?.items;

    if (items && items.length > 0) {
      // we have fileList
      this.dragItems.replace([...items]);
    }
  }

  handleDragLeave(_evt: DragEvent) {
    this.isDragHovered = false;
    // this.dragItems.clear();
  }
  handleDrop(_evt: DragEvent) {
    this.isDragHovered = false;
    this.dragItems.clear();

    // Drop on the file input also triggers a file change event
    // TODO: test cross-browser compatibility before re-enabling this direct drop handler
    // const fileList = evt.dataTransfer?.files;
    // this.beginUpload(fileList);
  }

  setUploadInput(fileList?: FileList | null) {
    if (!this.store) {
      console.warn('Cannot begin upload without a store.');
      return;
    }

    if (!fileList || fileList.length === 0)
      return;

    const inputFiles = [...fileList];

    const nonMediaFiles = inputFiles.filter(file => !isMediaFile(file));
    if (nonMediaFiles.length > 0) {
      this.dispatch(
        'NotificationService',  // target
        'notifyError',          // type
        pluralize(nonMediaFiles.length, 'file is not a media file', 'files are not media files'));
      return;
    }

    // let largeFiles = inputFiles.filter(file => isLargeFile(file));
    // if (largeFiles.length > 0) {
    //   this.dispatch(
    //     'NotificationService',  // target
    //     'notifyError',          // type
    //     pluralize(largeFiles.length, `file exceeds the maximum file size.`, 'files exceed the maximum file size.'));
    //   return;
    // }

    if (this.jobId)
      this.resetForm();

    // Replacing a job's source takes a single file, so segment grouping doesn't apply.
    const groups = this.isReplace
      ? inputFiles.map(file => ({ files: [file], title: undefined }))
      : groupRecordingSegments(inputFiles);

    groups.forEach(group => this.addFileItem(group.files, group.title));
    this.store.uploadGuidedTraining.handlePracticeFileSelected();
  }

  private createFileItem(files: File[], mergedTitle?: string) {
    return new UploadFileItemState(this.store, {
      teamId: () => this.teamId,
      jobId: () => this.jobId,
      folderId: () => this.folderId,
      files,
      mergedTitle
    });
  }

  addFileItem(files: File[], mergedTitle?: string) {
    this.fileInputList.push(this.createFileItem(files, mergedTitle));
  }

  splitFileItem(id: string) {
    const itemIndex = this.fileInputList.findIndex(item => item.id === id);
    const item = this.fileInputList[itemIndex];
    if (!item?.isMerged) return;

    const replacements = item.files.map(file => {
      const replacement = this.createFileItem([file]);
      replacement.specialityInput.loadValue(item.specialityInput.value);
      replacement.languageInput.loadValue(item.languageInput.value);
      replacement.levelInput.loadValue(item.levelInput.value);
      replacement.videoTypeInput.loadValue(item.videoTypeInput.value);
      return replacement;
    });

    this.fileInputList.splice(itemIndex, 1, ...replacements);
  }

  removeFileItem(id: string) {
    const itemIndex = this.fileInputList.findIndex(item => item.id === id);

    if (itemIndex > -1)
      this.fileInputList.splice(itemIndex, 1);
  }

  async submit() {
    if (!this.form)
      return;

    this.form.handleSubmit();

    if (this.form.error) {
      this.form.handleSubmitReject();
      return;
    }

    this.stagedItems.forEach(item => item.submit(task => {
      this.expandProgressBox(task.teamId || 'null');
      this.pageSessionTasks.push(task);
    }));

    this.form.handleSubmitResolve();
  }

  init() {
    // for the moment the tasks are merged when the page is initialized, the current session tasks and the global tasks should be separated
    const contextTasks = this.store?.uploadService?.tasks.filter(task => task.isActive);
    this.pageSessionTasks.push(...contextTasks);
    this.taskTeamIds.forEach(id => this.expandProgressBox(id));
  }

  reset() {
    this.pageSessionTasks.clear();
    this.form?.clear();
    this.fileInputList.clear();
  }

  resetForm() {
    this.form?.clear();
    this.fileInputList.clear();
  }
}
